import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wifi, Copy, Check, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { trackEvent } from '../../utils/analytics';
import { useHubProperty, hubPath } from './hubConfig';
import { HubEmailGate, hasHubAccess } from './HubEmailGate';
import { HubLogo } from './HubBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CopyRow = ({ label, value, field, copied, onCopy }) => (
  <div>
    <label className="block text-slate-400 text-xs mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-slate-700 rounded-lg px-4 py-3 min-w-0">
        <span data-testid={`wifi-${field}-value`} className="text-white font-mono text-lg break-all">{value}</span>
      </div>
      <Button data-testid={`wifi-copy-${field}-btn`} variant="outline" size="icon" onClick={() => onCopy(value, field)}
        className="border-slate-600 bg-transparent hover:bg-slate-700">
        {copied === field ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-slate-400" />}
      </Button>
    </div>
  </div>
);

const WifiPage = () => {
  const { slug, propertyId, hubProperty } = useHubProperty();
  const [wifi, setWifi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(hasHubAccess('wifi', propertyId));
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    trackEvent('wifi_page_view', { property_id: propertyId });
    fetch(`${API_URL}/api/public/wifi/${propertyId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setWifi)
      .finally(() => setLoading(false));
  }, [propertyId]);

  const copy = (text, field) => {
    navigator.clipboard?.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
    trackEvent('wifi_copy', { field, property_id: propertyId });
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-amber-400" /></div>;
  }

  return (
    <div data-testid="wifi-page" className="min-h-screen bg-slate-900 max-w-lg mx-auto">
      <div className="px-4 pt-6 pb-4">
        <Link to={hubPath(slug)} data-testid="hub-back-link" className="inline-flex items-center text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Hub
        </Link>
        <div className="text-center">
          <div className="mb-6"><HubLogo /></div>
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wifi className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Get Connected</h1>
          <p className="text-slate-400 text-sm">{hubProperty?.name || 'Capitol View Stays'}</p>
        </div>
      </div>

      <div className="px-4 pb-8">
        {!unlocked ? (
          <HubEmailGate source="wifi" propertyId={propertyId} slug={slug}
            prompt="Welcome! Enter your email to reveal the Wi-Fi details."
            buttonLabel="Get Wi-Fi Details" onUnlocked={() => setUnlocked(true)} />
        ) : (
          <div className="space-y-4">
            <Card data-testid="wifi-credentials" className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 space-y-4">
                <p className="text-green-400 text-sm text-center flex items-center justify-center gap-2">
                  <Check className="h-4 w-4" /> You're all set. Here are your Wi-Fi details:
                </p>
                <CopyRow label="Network Name" value={wifi?.network_name || 'Contact host'} field="network" copied={copied} onCopy={copy} />
                <CopyRow label="Password" value={wifi?.password || 'Contact host'} field="password" copied={copied} onCopy={copy} />
                <Button data-testid="wifi-copy-password-main-btn" onClick={() => copy(wifi?.password || '', 'password_main')}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-6 text-lg">
                  {copied === 'password_main' ? <><Check className="h-5 w-5 mr-2" />Copied!</> : <><Copy className="h-5 w-5 mr-2" />Copy Password</>}
                </Button>
                {wifi?.notes && <p className="text-slate-400 text-sm text-center">{wifi.notes}</p>}
              </CardContent>
            </Card>
            <div className="flex gap-2">
              <Link to={hubPath(slug, 'guidebook')} className="flex-1">
                <Button data-testid="wifi-to-guidebook-btn" variant="outline" className="w-full bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700">House Guide</Button>
              </Link>
              <Link to={hubPath(slug, 'local-guide')} className="flex-1">
                <Button data-testid="wifi-to-local-guide-btn" variant="outline" className="w-full bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700">Local Guide</Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 pb-8 text-center">
        <p className="text-slate-500 text-xs">Connection issues? <a href="tel:+15715075650" className="text-amber-400">Call (571) 507-5650</a></p>
      </div>
    </div>
  );
};

export default WifiPage;
